<script lang="ts" setup>
import getLocalizedRoute from '~/utils/getLocalizedRoute'
import { Routes } from '~/enums/Routes'

const { isPerkuJavTheme, isTarpininke, isNoriuLive, isWhitelabel } = useTheme()
const logos = await useAssetManager().getLogos()

const { t } = useLang()
const footerLinks = computed(() => [
  {
    route: getLocalizedRoute(Routes.Faq.Index),
    name: t('common.faq'),
  },
  ...(isPerkuJavTheme
    ? [
        {
          route: getLocalizedRoute(Routes.CategorySlug),
          name: t('common.all-products'),
        },
      ]
    : []),
  {
    route: getLocalizedRoute(Routes.Contacts),
    name: t('contacts.contacts'),
  },
  ...(isPerkuJavTheme
    ? [
        {
          route: getLocalizedRoute(Routes.PrivacyPolicy),
          name: t('nav.privacy-policy'),
        },
      ]
    : []),
  {
    route: getLocalizedRoute(Routes.PurchaseAgreement),
    name: t('nav.purchase-agreement'),
  },
])
</script>

<template>
  <div class="jav-faq-and-contacts w-full flex flex-wrap gap-7 justify-between">
    <NuxtLink v-if="!isWhitelabel" :to="getLocalizedRoute(Routes.Index)" class="lg:min-w-80">
      <img
        :src="isNoriuLive ? logos.logo : logos.logoWhite"
        class="logo"
        alt="Logo"
        :class="{
          'h-[71px] w-[219px] lg:w-auto': !isTarpininke,
        }" />
    </NuxtLink>
    <div class="main-info flex flex-1 flex-col lg:flex-row gap-8 flex-wrap justify-between basis-auto">
      <div class="basic-info">
        <p class="font-bold pb-6">{{ t('footer.general-information') }}</p>
        <ul>
          <li v-for="link in footerLinks" :key="link.name">
            <NuxtLink :to="link.route">{{ link.name }}</NuxtLink>
          </li>
        </ul>
      </div>
      <div v-if="isWhitelabel" class="services">
        <p class="font-bold pb-6">{{ t('common.services') }}</p>
        <ul>
          <li>
            <NuxtLink :to="getLocalizedRoute(isWhitelabel ? Routes.Marketplace : Routes.Market)">
              {{ t('nav.marketplace') }}
            </NuxtLink>
          </li>
          <li>
            <NuxtLink :to="getLocalizedRoute(Routes.Quotes)">{{ t('nav.quotes') }}</NuxtLink>
          </li>
          <li>
            <NuxtLink :to="getLocalizedRoute(Routes.Pro)">{{ t('common.site-pro') }}</NuxtLink>
          </li>
        </ul>
      </div>
      <div class="contacts flex flex-col">
        <hr v-if="isWhitelabel" class="contacts-divider !mb-6" />

        <p class="font-bold pb-6">{{ t('common.contact-us') }}</p>
        <ul class="mb-6">
          <li>
            <i class="fa-solid fa-envelope" />
            <a :href="`mailto:${t('footer.store-email')}`" target="_blank" v-html="t('footer.store-email')" />
          </li>
          <li v-if="!isWhitelabel">
            <i class="fa-brands fa-facebook" />
            <a :href="getFbGroupLink()" target="_blank">
              {{ t('nav.facebook_group') }}
            </a>
          </li>
        </ul>
        <hr v-if="isWhitelabel" class="contacts-divider" />
      </div>
      <PaymentDelivery />
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.jav-faq-and-contacts {
  ul {
    li {
      @apply mb-6 last:mb-0 text-sm;

      a {
        @apply text-white dark:text-black;
      }
    }
  }
}

.wl {
  .jav-faq-and-contacts {
    .contacts-divider {
      @apply border-neutral5 w-full max-w-full flex lg:hidden my-0;
    }

    ul {
      li a,
      i {
        @apply text-text2 dark:text-dmText2;
      }
    }
  }
}

.nl {
  .jav-faq-and-contacts {
    li a,
    i,
    p {
      @apply text-text1 dark:text-dmText1;
    }

    li > a {
      @apply font-medium;
    }
  }
}

.ae {
  .jav-faq-and-contacts {
    li a,
    i,
    p {
      @apply text-text1;
    }

    li > a {
      @apply font-medium;
    }
  }
}
</style>

<style lang="postcss">
.jav-faq-and-contacts {
  hr {
    margin: 1.5rem 0;
    border-top: 1px solid theme('colors.gray7');
  }

  .contacts {
    li {
      display: flex;
      margin-bottom: 1rem;
      align-items: center;

      a {
        padding: 0.5rem 0;
        font-size: 0.875rem;
        line-height: 0.875rem;
      }
    }

    hr {
      max-width: 215px;
      margin: 0.75rem 0 1.5rem;
    }
  }

  .contacts {
    li {
      display: flex;
      align-items: center;
      color: theme('colors.white');
      font-weight: bold;

      i {
        padding-right: 10px;
        font-size: 1rem;
      }
    }
  }
}
</style>
